// Section management service - Updated to match Postman Collection
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

export interface Section {
  id: number;
  checkpoint_id: number;
  section_name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

// Legacy interface for compatibility
export interface SectionData {
  section_id: number;
  checkpoint_id: number;
  section_name: string;
  description?: string;
  is_active: boolean;
  created_date: string;
  updated_date?: string;
}

export interface SectionFormData {
  checkpoint_id: number;
  section_name: string;
  description?: string;
}

class SectionService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - simple URL construction
    let url = baseUrl + endpoint;
    url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
    url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
    
    // Get access token if available (same pattern as api.ts)
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      // Silent fail for token acquisition
    }
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Convert API response to legacy format
  private mapToLegacyFormat(section: Section): SectionData {
    return {
      section_id: section.id,
      checkpoint_id: section.checkpoint_id,
      section_name: section.section_name,
      description: section.description,
      is_active: true, // Default value for legacy compatibility
      created_date: section.created_at,
      updated_date: section.updated_at
    };
  }

  // Get all sections (legacy method name)
  async getAllSections(): Promise<SectionData[]> {
    const response = await this.request<{data: Section[]} | Section[]>(API_ENDPOINTS.SECTIONS);
    const sections = 'data' in response ? response.data : response;
    return (Array.isArray(sections) ? sections : []).map(section => this.mapToLegacyFormat(section));
  }

  // Get all sections (new method name)
  async getSections(): Promise<Section[]> {
    const response = await this.request<{data: Section[]} | Section[]>(API_ENDPOINTS.SECTIONS);
    return 'data' in response ? response.data : response;
  }

  // Get section by ID
  async getSection(id: number): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTION_BY_ID(id));
  }

  // Create new section
  async createSection(data: SectionFormData): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTIONS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update section
  async updateSection(id: number, data: Partial<SectionFormData>): Promise<Section> {
    return this.request<Section>(API_ENDPOINTS.SECTION_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete section
  async deleteSection(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.SECTION_BY_ID(id), {
      method: 'DELETE',
    });
  }

  // Validation methods for legacy compatibility
  validateSectionName(name: string): { isValid: boolean; message?: string } {
    if (!name || name.trim().length < 2) {
      return { isValid: false, message: 'Section name must be at least 2 characters long' };
    }
    if (name.length > 100) {
      return { isValid: false, message: 'Section name must be less than 100 characters' };
    }
    return { isValid: true };
  }

  validateDescription(description?: string): { isValid: boolean; message?: string } {
    if (description && description.length > 500) {
      return { isValid: false, message: 'Description must be less than 500 characters' };
    }
    return { isValid: true };
  }
}

// Export singleton instance
export const sectionService = new SectionService();
export default sectionService;
